import type { ManuscriptNode, NodesResponse } from "@contract";
import { buildPath } from "@contract";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/fetcher";
import { Button } from "../../components/control/Button";
import { Radio } from "../../components/control/Radio";
import { ErrorBanner } from "../../components/feedback/ErrorBanner";
import { Dialog } from "../../components/overlay/Dialog";

const PAGE_SIZE = 100;
const GROUP = "split-node";

export function SplitDialog({
  bookId,
  from,
  to,
  onClose,
  onSplit,
}: {
  bookId: string;
  from: number;
  to: number;
  onClose: () => void;
  onSplit: (node: ManuscriptNode) => void;
}) {
  const { t } = useTranslation("chapters");
  const [page, setPage] = useState(from + 1);
  const [selected, setSelected] = useState<ManuscriptNode | null>(null);
  const query = useQuery({
    queryKey: ["splitNodes", bookId, page, to],
    queryFn: () =>
      api<NodesResponse>(
        `${buildPath("/books/:id/nodes", { id: bookId })}?from=${page}&to=${Math.min(page + PAGE_SIZE, to)}`,
      ),
    enabled: page < to,
    retry: false,
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={t("splitTitle")}
      width="lg"
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            disabled={!selected}
            onClick={() => {
              if (selected) onSplit(selected);
            }}
          >
            {t("splitHere")}
          </Button>
        </>
      }
    >
      <p className="mb-4 text-ink-2">{t("splitHelp")}</p>
      <ErrorBanner
        error={query.error}
        action={
          <Button variant="quiet" onClick={() => void query.refetch()}>
            {t("retry")}
          </Button>
        }
      />
      {query.isPending ? <p role="status">{t("loading")}</p> : null}
      <div className="flex max-h-80 flex-col gap-3 overflow-y-auto">
        {query.data?.nodes.map((node) => (
          <Radio
            key={node.id}
            name={GROUP}
            checked={selected?.id === node.id}
            label={node.text.trim() || t("emptyParagraph")}
            description={t("paragraphPosition", { position: node.ordinal + 1 })}
            onChange={() => setSelected(node)}
            className="break-words border-b border-line-soft pb-3 font-serif"
          />
        ))}
      </div>
      <div className="mt-4 flex flex-wrap justify-between gap-2">
        <Button
          variant="quiet"
          disabled={page === from + 1}
          onClick={() => setPage(Math.max(from + 1, page - PAGE_SIZE))}
        >
          {t("previous")}
        </Button>
        <Button
          variant="quiet"
          disabled={page + PAGE_SIZE >= to}
          onClick={() => setPage(page + PAGE_SIZE)}
        >
          {t("next")}
        </Button>
      </div>
    </Dialog>
  );
}
